import React, { Fragment, Suspense, useContext, useEffect, useState } from 'react';
import { Spinner, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
    disableReminderYours,
    disableRemindersAll,
    useGetRemindersAll,
    fetchRemindersYours,
    fetchRemindersAll,
} from '../../utils/dashboard-hooks';
import { faBan, faBell } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dateFormat from 'dateformat';
import { NoDataComponent } from '../NoDataComponent';
import ErrorBoundary from '../ErrorBoundary';
import { InfoModal } from '../InfoModal';
import { ErrorComponent } from '../ErrorComponent';
import { date } from '../../utils/Enums/Common';
import { BranchesContext } from '../../context/BranchesContext';
import { AuthContext } from '../../context/AuthContext';
import { Role } from '../../utils/Enums/Roles';
import { Reminder } from '../../models/dashboard-models';
import StepWizard from 'react-step-wizard';
import { CustomStepWizardNav } from '../CustomStepWizardNav';
import { resourceCache } from 'use-async-resource';

export const Reminders = ({
    setHasExpiringReminder,
}: {
    setHasExpiringReminder: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const { getReminders, fetchNewReminders } = useContext(BranchesContext);
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { hasPermission } = useContext(AuthContext);
    const [showModal, setShowModal] = useState(false);
    const [selectedReminderId, setSelectedReminderId] = useState(0);
    const [isDisableYours, setIsDisableYours] = useState(false);

    const reminders = getReminders();

    useEffect(() => {
        if (reminders.find((reminder) => dateFormat(reminder.endTime, date) === dateFormat(Date.now(), date))) {
            setHasExpiringReminder(true);
        }
    }, []);

    const onHide = async (isDelete: boolean) => {
        resourceCache(fetchRemindersYours).clear();
        if (isDelete === true) {
            isDisableYours ? disableReminderYours(selectedReminderId) : disableRemindersAll(selectedReminderId);
            resourceCache(fetchRemindersAll).clear();
            await fetchNewReminders();
        }
        return setShowModal(false);
    };
    return (
        <>
            <Tab.Pane eventKey='fourth' className='p-2 w-100 overflow-auto '>
                <ErrorBoundary fallback={<ErrorComponent errorText={t('branches')} />}>
                    <Suspense fallback={<Spinner animation='border' />}>
                        {hasPermission([Role.COORDINATOR]) ? (
                            <StepWizard
                                className='step-wizard px-0 d-flex flex-wrap w-100'
                                nav={
                                    <CustomStepWizardNav
                                        steps={['allReminders', 'yourReminders']}
                                        customContainerClass='reminders-wizard-nav'
                                    />
                                }
                                transitions={{
                                    enterRight: '',
                                    enterLeft: '',
                                    exitRight: '',
                                    exitLeft: '',
                                }}
                            >
                                <AllReminders {...{ setShowModal, setSelectedReminderId, setIsDisableYours }} />
                                <YourReminders
                                    {...{ setShowModal, setSelectedReminderId, reminders, setIsDisableYours }}
                                />
                            </StepWizard>
                        ) : (
                            <YourReminders {...{ setShowModal, setSelectedReminderId, reminders, setIsDisableYours }} />
                        )}

                        <InfoModal show={showModal} {...{ onHide }}></InfoModal>
                    </Suspense>
                </ErrorBoundary>
            </Tab.Pane>
        </>
    );
};

const AllReminders = ({
    setShowModal,
    setSelectedReminderId,
    setIsDisableYours,
}: {
    setShowModal: (p: boolean) => void;
    setSelectedReminderId: (n: number) => void;
    setIsDisableYours: (isDisableYours: boolean) => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [getAllReminders] = useGetRemindersAll();
    const reminders = getAllReminders();

    return (
        <>
            {reminders ? (
                reminders.map((reminder, index) => (
                    <Fragment key={index}>
                        {!reminder.disabled && (
                            <div className='d-flex flex-column w-100 p-1 reminder my-2' key={index}>
                                <div className='d-flex justify-content-between align-items-center w-100 p-1'>
                                    <div>
                                        <FontAwesomeIcon icon={faBell} className='fa-fw' color='orange'></FontAwesomeIcon>
                                        <span className='mb-0 px-1'>{reminder.branchName}</span>
                                    </div>
                                    <div className='d-flex align-items-center'>
                                        <span className='mb-0 px-1'>Ends {dateFormat(reminder.endTime, date)}</span>
                                        <FontAwesomeIcon
                                            icon={faBan}
                                            className='fa-fw'
                                            role='button'
                                            color='red'
                                            onClick={() => {
                                                setShowModal(true);
                                                setSelectedReminderId(reminder.id);
                                                setIsDisableYours(false);
                                            }}
                                        ></FontAwesomeIcon>
                                    </div>
                                </div>
                                <div className='p-1'>
                                    <p className='fw-bold mb-0'>{reminder.comment=="Test period end reminder" || reminder.comment=="Unpause device" ?t(reminder.comment):reminder.comment}</p>
                                </div>
                            </div>
                        )}
                    </Fragment>
                ))
            ) : (
                <div className='h-100 d-flex align-items-center w-100'>
                    <NoDataComponent noDataText={t('reminders')}></NoDataComponent>
                </div>
            )}
        </>
    );
};

const YourReminders = ({
    reminders,
    setShowModal,
    setSelectedReminderId,
    setIsDisableYours,
}: {
    reminders: Reminder[];
    setShowModal: (p: boolean) => void;
    setSelectedReminderId: (n: number) => void;
    setIsDisableYours: (isDisableYours: boolean) => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    return (
        <>
            {reminders ? (
                reminders
                    .filter((reminder) => !reminder.disabled)
                    .map((reminder, index) => (
                        <div className='d-flex flex-column w-100 p-1 reminder my-2' key={index}>
                            <div className='d-flex justify-content-between align-items-center w-100 p-1'>
                                <div>
                                    <FontAwesomeIcon icon={faBell} className='fa-fw' color='orange'></FontAwesomeIcon>
                                    <span className='mb-0 px-1'>{reminder.branchName}</span>
                                </div>
                                <div className='d-flex align-items-center'>
                                    <span className='mb-0 px-1'>Ends {dateFormat(reminder.endTime, date)}</span>
                                    <FontAwesomeIcon
                                        icon={faBan}
                                        className='fa-fw'
                                        role='button'
                                        color='red'
                                        onClick={() => {
                                            setShowModal(true);
                                            setSelectedReminderId(reminder.id);
                                            setIsDisableYours(true);
                                        }}
                                    ></FontAwesomeIcon>
                                </div>
                            </div>
                            <div className='p-1'>
                                <p className='fw-bold mb-0'>{reminder.comment=="Test period end reminder" || reminder.comment=="Unpause device" ?t(reminder.comment):reminder.comment}</p>
                            </div>
                        </div>
                    ))
            ) : (
                <div className='h-100 d-flex align-items-center w-100'>
                    <NoDataComponent noDataText={t('reminders')}></NoDataComponent>
                </div>
            )}
        </>
    );
};
